import {
  CHAT_ALERT_CONTENT_CLASS_NAME,
  CHAT_DIALOG_HEADER_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
  CHAT_DIALOG_FOOTER_CLASS_NAME,
} from "@/features/chat/chat-dialog-styles";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileSignature, Loader2 } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { queryClient } from "@/ajax/queryClient";
import { useTRPC, type RouterOutputs } from "@/trpc";
import { SendFormDialog } from "./send-form-dialog";

type FormAssignmentDraft =
  RouterOutputs["organizationForm"]["listFormAssignmentDrafts"][number];

interface DraftFormAssignmentGateProps {
  organizationBusinessUnitId?: string;
  recipientUserId?: string;
  canManageJobPosts?: boolean;
  documentSigningEnabled?: boolean;
  providerDisplayName?: string;
  onBlockingChange?: (blocking: boolean) => void;
  onFormAssignmentSent?: (chatThreadId: string) => void;
}

export function DraftFormAssignmentGate({
  organizationBusinessUnitId,
  recipientUserId,
  canManageJobPosts = false,
  documentSigningEnabled = false,
  providerDisplayName,
  onBlockingChange,
  onFormAssignmentSent,
}: DraftFormAssignmentGateProps) {
  const trpc = useTRPC();
  const [continueDraft, setContinueDraft] =
    useState<FormAssignmentDraft | null>(null);
  const [discardDraft, setDiscardDraft] = useState<FormAssignmentDraft | null>(
    null,
  );

  const draftsInput = useMemo(
    () => ({
      organizationBusinessUnitId: organizationBusinessUnitId ?? "",
      recipientUserId: recipientUserId ?? "",
    }),
    [organizationBusinessUnitId, recipientUserId],
  );
  const enabled = Boolean(
    organizationBusinessUnitId &&
      recipientUserId &&
      canManageJobPosts &&
      documentSigningEnabled,
  );

  const { data: drafts } = useQuery(
    trpc.organizationForm.listFormAssignmentDrafts.queryOptions(draftsInput, {
      enabled,
    }),
  );

  const isBlocking = enabled && (drafts?.length ?? 0) > 0;

  useEffect(() => {
    onBlockingChange?.(isBlocking);
  }, [isBlocking, onBlockingChange]);

  const cancelMutation = useMutation(
    trpc.organizationForm.cancelFormAssignment.mutationOptions({
      onSuccess: async () => {
        toast.success("Draft discarded");
        setDiscardDraft(null);
        await queryClient.invalidateQueries(
          trpc.organizationForm.listFormAssignmentDrafts.queryOptions(
            draftsInput,
          ),
        );
      },
      onError: (err) => {
        toast.error(err.message || "Failed to discard draft");
      },
    }),
  );

  if (!organizationBusinessUnitId || !recipientUserId) {
    return null;
  }
  const orgId = organizationBusinessUnitId;
  const userId = recipientUserId;

  const newest = drafts?.[0];
  const moreCount = (drafts?.length ?? 0) - 1;

  return (
    <>
      {isBlocking && newest && (
        <div
          role="status"
          aria-live="polite"
          className="mx-4 mt-3 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-xs sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-amber-200 bg-amber-100 text-amber-700">
              <FileSignature className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm leading-6 text-amber-900 [overflow-wrap:anywhere]">
                You have an unfinished form for{" "}
                <span className="font-medium">
                  {providerDisplayName ?? "this provider"}
                </span>
                :{" "}
                <span className="font-semibold text-amber-950">
                  &ldquo;{newest.formName}&rdquo;
                </span>
              </p>
              {moreCount > 0 && (
                <span className="mt-1 inline-flex items-center rounded-lg bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                  +{moreCount} more waiting
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 sm:shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="min-h-12 text-slate-600 hover:bg-white hover:text-slate-900"
              onClick={() => setDiscardDraft(newest)}
            >
              Discard
            </Button>
            <Button
              size="sm"
              className="min-h-12"
              onClick={() => setContinueDraft(newest)}
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {continueDraft && (
        <SendFormDialog
          key={continueDraft.assignmentId}
          open
          onOpenChange={(open) => {
            if (open) {
              return;
            }
            setContinueDraft(null);
            void queryClient.invalidateQueries(
              trpc.organizationForm.listFormAssignmentDrafts.queryOptions(
                draftsInput,
              ),
            );
          }}
          organizationBusinessUnitId={orgId}
          recipientUserId={userId}
          resumeDraft={{
            assignmentId: continueDraft.assignmentId,
            formId: continueDraft.formId,
            orgSignerCompleted: continueDraft.orgSignerCompleted,
            formSubmissionId: continueDraft.formSubmissionId,
          }}
          onSent={(chatThreadId) => onFormAssignmentSent?.(chatThreadId)}
        />
      )}

      <AlertDialog
        open={!!discardDraft}
        onOpenChange={(open) => !open && setDiscardDraft(null)}
      >
        <AlertDialogContent className={CHAT_ALERT_CONTENT_CLASS_NAME}>
          <AlertDialogHeader className={CHAT_DIALOG_HEADER_CLASS_NAME}>
            <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
            <AlertDialogDescription
              className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}
            >
              Signing progress on &ldquo;{discardDraft?.formName}&rdquo; will be
              lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={CHAT_DIALOG_FOOTER_CLASS_NAME}>
            <AlertDialogCancel>Keep draft</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                discardDraft &&
                cancelMutation.mutate({
                  organizationBusinessUnitId: orgId,
                  assignmentId: discardDraft.assignmentId,
                })
              }
              disabled={cancelMutation.isPending}
              className={buttonVariants({ variant: "destructive" })}
            >
              {cancelMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Discard"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
